import { useState } from "react";
import { UserPlus, Users, MessageCircle, X, Check } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { ScrollArea } from "@/components/ui/scroll-area";
import type { Contact } from "@/hooks/useMessaging";

interface NewChatDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  contacts: Contact[];
  onAddContact: (email: string) => Promise<string | null>;
  onCreateChat: (participantIds: string[], isGroup: boolean, groupName?: string) => void;
}

export const NewChatDialog = ({ open, onOpenChange, contacts, onAddContact, onCreateChat }: NewChatDialogProps) => {
  const [tab, setTab] = useState<"contact" | "chat" | "group">("chat");
  const [email, setEmail] = useState("");
  const [adding, setAdding] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [groupName, setGroupName] = useState("");

  const handleAddContact = async () => {
    if (!email.trim()) return;
    setAdding(true);
    await onAddContact(email.trim());
    setEmail("");
    setAdding(false);
  };

  const toggleSelect = (id: string) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const handleCreate = () => {
    if (tab === "chat" && selectedIds.length === 1) {
      onCreateChat(selectedIds, false);
    } else if (tab === "group" && selectedIds.length >= 1) {
      onCreateChat(selectedIds, true, groupName || undefined);
    }
    setSelectedIds([]);
    setGroupName("");
  };

  const getContactName = (c: Contact) => {
    if (c.profile?.first_name || c.profile?.last_name) return `${c.profile.first_name || ""} ${c.profile.last_name || ""}`.trim();
    return c.profile?.email || "Utente";
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-background/90 backdrop-blur-2xl border-border/30 max-w-sm mx-auto rounded-3xl">
        <DialogHeader>
          <DialogTitle className="text-center text-lg font-bold">Nuova conversazione</DialogTitle>
        </DialogHeader>

        {/* Tabs */}
        <div className="flex gap-1 p-1 bg-muted/40 rounded-xl">
          {[
            { id: "contact" as const, label: "Contatto", icon: UserPlus },
            { id: "chat" as const, label: "Chat", icon: MessageCircle },
            { id: "group" as const, label: "Gruppo", icon: Users },
          ].map(t => (
            <button
              key={t.id}
              onClick={() => { setTab(t.id); setSelectedIds([]); }}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-sm font-medium transition-all ${
                tab === t.id ? "bg-background shadow-sm text-foreground" : "text-muted-foreground"
              }`}
            >
              <t.icon className="w-4 h-4" />
              {t.label}
            </button>
          ))}
        </div>

        {/* Add Contact */}
        {tab === "contact" && (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">Aggiungi un contatto tramite e-mail</p>
            <div className="flex gap-2">
              <Input
                placeholder="email@esempio.com"
                value={email}
                onChange={e => setEmail(e.target.value)}
                className="flex-1 bg-muted/40 border-none rounded-xl"
                onKeyDown={e => e.key === "Enter" && handleAddContact()}
              />
              <Button onClick={handleAddContact} disabled={adding} size="sm" className="rounded-xl px-4">
                {adding ? "..." : "Aggiungi"}
              </Button>
            </div>
          </div>
        )}

        {/* Chat / Group selection */}
        {(tab === "chat" || tab === "group") && (
          <div className="space-y-3">
            {tab === "group" && (
              <Input
                placeholder="Nome del gruppo"
                value={groupName}
                onChange={e => setGroupName(e.target.value)}
                className="bg-muted/40 border-none rounded-xl"
              />
            )}
            <p className="text-sm text-muted-foreground">
              {tab === "chat" ? "Seleziona un contatto" : "Seleziona i partecipanti"}
            </p>
            <ScrollArea className="max-h-60">
              {contacts.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-6">
                  Nessun contatto. Aggiungine uno prima!
                </p>
              ) : (
                contacts.map(c => {
                  const selected = selectedIds.includes(c.contact_user_id);
                  return (
                    <button
                      key={c.id}
                      onClick={() => {
                        if (tab === "chat") {
                          setSelectedIds([c.contact_user_id]);
                        } else {
                          toggleSelect(c.contact_user_id);
                        }
                      }}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all ${
                        selected ? "bg-primary/10" : "hover:bg-muted/40"
                      }`}
                    >
                      <Avatar className="w-10 h-10">
                        <AvatarImage src={c.profile?.avatar_url || undefined} />
                        <AvatarFallback className="bg-primary/10 text-primary text-xs">
                          {getContactName(c).charAt(0).toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      <span className="flex-1 text-left text-sm font-medium">{getContactName(c)}</span>
                      {selected && <Check className="w-4 h-4 text-primary" />}
                    </button>
                  );
                })
              )}
            </ScrollArea>
            {selectedIds.length > 0 && (
              <Button onClick={handleCreate} className="w-full rounded-xl">
                {tab === "chat" ? "Apri Chat" : `Crea Gruppo (${selectedIds.length})`}
              </Button>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
